<!--
  Сообщение в чате: показывает автора, текст, вложения, статус доставки и ошибку.
-->
<template>
  <div
    class="message"
    :class="{
      'message-outgoing': outgoingMessage,
      'message-incoming': !outgoingMessage,
      'message-failed': message.delivery_status === 'failed',
    }"
  >
    <div class="message-content">
      <div
        class="message-header"
        :class="{
          'message-header_user': outgoingMessage,
        }"
      >
        <strong class="sender-name">
          {{ senderName }}
        </strong>
      </div>
      <div
        class="message-bubble"
        role="button"
        tabindex="0"
        @click="toggleTimestampTooltip"
        @keydown.enter.prevent="toggleTimestampTooltip"
        @keydown.space.prevent="toggleTimestampTooltip"
      >
        <p v-if="message.text" class="message-text">{{ message.text }}</p>
        <div
          v-if="message.attachments?.length"
          class="message-attachments"
        >
          <template
            v-for="attachment in message.attachments"
            :key="attachment.id"
          >
            <ChatAudioAttachment
              v-if="isAudioAttachment(attachment)"
              :attachment="attachment"
              :variant="audioVariant"
            />
            <ChatAttachmentItem
              v-else
              :attachment="attachment"
              @preview="openAttachmentPreview"
            />
          </template>
        </div>
        <span class="message-meta">
          <span class="message-time">{{ displayedTimestamp }}</span>
          <ChatDeliveryStatus
            :visible="deliveryStatusVisible"
            :status="message.delivery_status"
            :error-text="deliveryErrorText"
            :error-open="deliveryErrorOpen"
            @toggle-error="toggleDeliveryError"
          />
        </span>
        <div
          v-if="deliveryErrorOpen"
          class="message-delivery-error"
        >
          {{ deliveryErrorText }}
        </div>
      </div>
      <button
        v-if="messageRetryVisible"
        class="message-retry"
        type="button"
        :disabled="retrying"
        @click="retryMessage"
      >
        Повторить
      </button>
    </div>
    <ChatAttachmentPreviewModal
      v-if="previewAttachment"
      :attachment="previewAttachment"
      @close="closeAttachmentPreview"
    />
  </div>
</template>

<script>
import ChatAttachmentItem from "@/components/Chat/AttachmentItem.vue";
import ChatAudioAttachment from "@/components/Chat/AudioAttachment.vue";
import ChatAttachmentPreviewModal from "@/components/Chat/AttachmentPreviewModal.vue";
import ChatDeliveryStatus from "@/components/Chat/DeliveryStatus.vue";
import { isAudio } from "@/utils/fileUtils";
import { formatDateAndTime } from "@/utils/dateUtils";

export default {
  name: "ChatMessageItem",

  components: {
    ChatAttachmentItem,
    ChatAudioAttachment,
    ChatAttachmentPreviewModal,
    ChatDeliveryStatus,
  },

  props: {
    message: {
      type: Object,
      required: true,
    },
    conversationName: {
      type: String,
      default: "",
    },
    retrying: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["retry"],

  data() {
    return {
      deliveryErrorOpen: false,
      previewAttachment: null,
      timestampExpanded: false,
    };
  },

  computed: {
    outgoingMessage() {
      return this.message.sender_type === "system";
    },

    senderName() {
      if (this.outgoingMessage) {
        return this.message.created_by?.initials || "Оператор";
      }

      return this.conversationName;
    },

    deliveryStatusVisible() {
      return this.outgoingMessage && !!this.message.delivery_status;
    },

    messageRetryVisible() {
      return this.outgoingMessage && this.message.delivery_status === "failed";
    },

    deliveryErrorText() {
      return this.message.delivery_error || "Не удалось отправить сообщение";
    },

    displayedTimestamp() {
      if (this.timestampExpanded) {
        return formatDateAndTime(this.message.created_at);
      }

      return this.formatTime(this.message.created_at);
    },

    audioVariant() {
      return this.outgoingMessage ? "outgoing" : "incoming";
    },

  },

  watch: {
    "message.delivery_status"(value) {
      if (value !== "failed") this.deliveryErrorOpen = false;
    },
  },

  methods: {
    toggleDeliveryError() {
      this.timestampExpanded = false;
      this.deliveryErrorOpen = !this.deliveryErrorOpen;
    },

    toggleTimestampTooltip() {
      this.deliveryErrorOpen = false;
      this.timestampExpanded = !this.timestampExpanded;
    },

    retryMessage() {
      this.deliveryErrorOpen = false;
      this.timestampExpanded = false;
      this.$emit("retry", this.message);
    },

    openAttachmentPreview(attachment) {
      this.deliveryErrorOpen = false;
      this.timestampExpanded = false;
      this.previewAttachment = attachment;
    },

    closeAttachmentPreview() {
      this.previewAttachment = null;
    },

    formatTime(dateStr) {
      return new Date(dateStr).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    isAudioAttachment(attachment) {
      return isAudio(attachment);
    },

  },
};
</script>

<style scoped>
.message {
  display: flex;
  min-width: 0;
  max-width: 80%;
}

.message-outgoing {
  align-self: flex-end;
}

.message-incoming {
  align-self: flex-start;
}

.message-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 0;
}

.message-header_user {
  justify-content: flex-end;
}

.sender-name {
  font-size: 11px;
  color: #666;
  font-weight: 500;
  margin: 0;
}

.message-bubble {
  position: relative;
  padding: 8px 14px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  min-width: 60px;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.message-bubble:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}

.message-outgoing .message-bubble {
  background: linear-gradient(135deg, #b8f2e6 0%, #a0eac2 100%);
  border-top-right-radius: 3px;
}

.message-incoming .message-bubble {
  background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
  border-top-left-radius: 3px;
}

.message-failed.message-outgoing .message-bubble {
  background: #edf0ee;
  color: #666;
}

.message-text {
  display: inline;
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
  word-wrap: break-word;
  white-space: pre-wrap;
  min-height: 1.4em;
}

.message-attachments {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-text + .message-attachments {
  margin-top: 8px;
}

.message-meta {
  align-items: center;
  float: right;
  display: inline-flex;
  gap: 2px;
  line-height: 1;
  margin: 4px -6px 0 4px;
  min-width: max-content;
}

.message-time {
  font-size: 10px;
  color: #666;
  white-space: nowrap;
}

.message-delivery-error {
  clear: both;
  margin-top: 7px;
  max-width: 240px;
  padding: 7px 9px;
  border-radius: 6px;
  background: #fff;
  color: var(--color-danger);
  font-size: 11px;
  line-height: 1.35;
  overflow-wrap: anywhere;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.message-retry {
  align-self: flex-end;
  background: transparent;
  border: 0;
  color: var(--color-danger);
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  padding: 0 4px;
}

.message-retry:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 768px) {
  .message {
    max-width: 88%;
  }
}
</style>
